import React from "react"
import { StoreGroup } from "lib/types/generated/graphql-types"
import MultiSelect from "../Ui/Form/MultiSelect"
import PrimaryButton from "../Ui/Buttons/PrimaryButton"
import SecondaryButton from "../Ui/Buttons/SecondaryButton"
import FilterSectionDashboard from "../DashboardSales/FilterSelectionDashboard"
import { Option } from "lib/types/common"
import { getCountryName } from "helpers/countries"
import dayjs from "dayjs"
import {
  DatePickerWrapper,
  Divider,
  FilterContainer,
  FilterRow,
  FiltersGrid,
  IconWrapper,
  MultiSelectWrapper,
  PaginationButton,
  PaginationSection,
  PeriodSelection,
  SelectWrapper
} from "./InsightsFilters.styled"
import { ReactComponent as StoreGroupIcon } from "images/icons/circle-nodes.svg"
import { ReactComponent as StoreMarketIcon } from "images/icons/earth.svg"
import { ReactComponent as ReasonIcon } from "images/icons/circle-exclamation.svg"
import { Loader } from "../Ui/Loader"
import SingleSelect from "../Ui/Form/SingleSelect"
import { ReactComponent as TimeZoneIcon } from "images/icons/calendar-clock.svg"
import { Common } from "lib/types"

type Range = 30 | 7 | 2 | 1 | 0

type Props = {
  storeGroups: StoreGroup[]
  selectedStoreGroups: Option[]
  selectedStoreMarkets: Option[]
  selectedReasons: Option[]
  startDate: string
  endDate: string
  loading: boolean
  numberOfHits: string
  numberOfHitsOptions: string[]
  filteredDays: number
  onStoreGroupsChange: (groups: Option[]) => void
  onStoreMarketsChange: (markets: Option[]) => void
  onReasonsChange: (reasons: Option[]) => void
  onDateRangeChange: (start: string, end: string) => void
  onNumberOfHitsChange: (hits: string) => void
  onFilteredDaysChange: (days: number) => void
  onTimeZoneChange: (timeZone: Common.Option) => void
}

const PeriodButton = ({
  range,
  currentRange,
  onClick,
  label
}: {
  range: Range
  currentRange: Range
  onClick: (range: Range) => void
  label: string
}) => {
  const isActive = range === currentRange

  if (isActive) {
    return <PrimaryButton handleClick={() => onClick(range)}>{label}</PrimaryButton>
  }

  return <SecondaryButton handleClick={() => onClick(range)}>{label}</SecondaryButton>
}

const InsightsFilters = ({
  storeGroups,
  selectedStoreGroups,
  selectedStoreMarkets,
  selectedReasons,
  startDate,
  endDate,
  loading,
  numberOfHits,
  numberOfHitsOptions,
  filteredDays,
  onStoreGroupsChange,
  onStoreMarketsChange,
  onReasonsChange,
  onDateRangeChange,
  onNumberOfHitsChange,
  onFilteredDaysChange,
  onTimeZoneChange
}: Props) => {
  const timeZones = Intl.supportedValuesOf("timeZone")

  const getStoreMarketOptions = () => {
    const storeMarkets = storeGroups
      ?.map((storeGroup: StoreGroup) => storeGroup.storeMarkets)
      .flat()
    const countryCodes = storeMarkets?.map((storeMarket) => storeMarket?.countryCode)
    const uniqueCountryCodes = [...new Set(countryCodes)] as string[]
    return uniqueCountryCodes
      .map(
        (code: string) =>
          ({
            value: code,
            label: getCountryName(code)
          }) as Option
      )
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  const getReasonOptions = (): Option[] => {
    return [
      { value: "PENDING", label: "Pending" },
      { value: "ON_HOLD", label: "On Hold" },
      { value: "REJECTED", label: "Rejected" },
    ]
  }

  const handleRangeChange = (newRange: Range) => {
    onFilteredDaysChange(newRange)
    const end = dayjs()
    const start = dayjs()
      .subtract(newRange - 1, "day")
      .startOf("day")
    const formattedStartDate = start.format("YYYY-MM-DDTHH:mm")
    const formattedEndDate = end.format("YYYY-MM-DDTHH:mm")
    onDateRangeChange(formattedStartDate, formattedEndDate)
  }

  const handleYesterdayChange = () => {
    onFilteredDaysChange(2)
    const end = dayjs().subtract(1, "day").endOf("day")
    const start = dayjs().subtract(1, "day").startOf("day")
    const formattedStartDate = start.format("YYYY-MM-DDTHH:mm")
    const formattedEndDate = end.format("YYYY-MM-DDTHH:mm")
    onDateRangeChange(formattedStartDate, formattedEndDate)
  }

  const handleCustomDateChange = (newStartDate: string, newEndDate: string) => {
    onFilteredDaysChange(0)
    onDateRangeChange(newStartDate, newEndDate)
  }

  return (
    <FilterContainer>
      {/* First Row: Store Groups, Markets, and Search */}
      <FilterRow>
        <FiltersGrid>
          <MultiSelectWrapper>
            <IconWrapper>
              <TimeZoneIcon />
            </IconWrapper>
            <SingleSelect
              options={timeZones.map((timeZone) => ({ value: timeZone, label: timeZone }))}
              setValue={onTimeZoneChange}
              defaultValue={{
                value: Intl.DateTimeFormat().resolvedOptions().timeZone,
                label: Intl.DateTimeFormat().resolvedOptions().timeZone
              }}
            />
          </MultiSelectWrapper>
          <MultiSelectWrapper>
            <IconWrapper>
              <StoreGroupIcon />
            </IconWrapper>
            <MultiSelect
              selectAllEnabled={true}
              options={storeGroups?.map((storeGroup: StoreGroup) => ({
                value: storeGroup.id,
                label: storeGroup.name
              }))}
              placeholder="All store groups"
              value={selectedStoreGroups}
              setValue={onStoreGroupsChange}
            />
          </MultiSelectWrapper>

          {getStoreMarketOptions()?.length > 0 && (
            <MultiSelectWrapper>
              <IconWrapper>
                <StoreMarketIcon />
              </IconWrapper>
              <MultiSelect
                placeholder="Select store market"
                options={getStoreMarketOptions()}
                value={selectedStoreMarkets}
                setValue={onStoreMarketsChange}
              />
            </MultiSelectWrapper>
          )}

          <MultiSelectWrapper>
            <IconWrapper>
              <ReasonIcon />
            </IconWrapper>
            <MultiSelect
              placeholder="Select reason"
              options={getReasonOptions()}
              value={selectedReasons}
              setValue={onReasonsChange}
            />
          </MultiSelectWrapper>
        </FiltersGrid>
      </FilterRow>

      {/* Date Range Filter */}
      <SelectWrapper>
        <DatePickerWrapper>
          <FilterSectionDashboard
            defaultFromDate={startDate}
            defaultToDate={endDate}
            setFromDate={(date) => handleCustomDateChange(date, endDate)}
            setToDate={(date) => handleCustomDateChange(startDate, date)}
          />
        </DatePickerWrapper>

        <Divider />

        <PeriodSelection>
          <PeriodButton
            range={1}
            currentRange={filteredDays as Range}
            onClick={handleRangeChange}
            label="Today"
          />
          <PeriodButton
            range={2}
            currentRange={filteredDays as Range}
            onClick={() => handleYesterdayChange()}
            label="Yesterday"
          />
          <PeriodButton
            range={7}
            currentRange={filteredDays as Range}
            onClick={handleRangeChange}
            label="7 days"
          />
          <PeriodButton
            range={30}
            currentRange={filteredDays as Range}
            onClick={handleRangeChange}
            label="30 days"
          />
          {loading && <Loader color="black" />}
        </PeriodSelection>

        <Divider />

        <PaginationSection>
          <span style={{ marginRight: "8px", fontSize: "14px", color: "#666" }}>Show:</span>
          {numberOfHitsOptions.map((hits) => (
            <PaginationButton
              key={hits}
              onClick={() => onNumberOfHitsChange(hits)}
              disabled={loading}
              isActive={hits === numberOfHits}
            >
              {hits}
            </PaginationButton>
          ))}
        </PaginationSection>
      </SelectWrapper>
    </FilterContainer>
  )
}

export default InsightsFilters
